import { useEffect, useMemo, useRef, useState } from "react";

type NetworkReason =
  | "init"
  | "browser-online"
  | "browser-offline"
  | "probe-success"
  | "probe-failed"
  | "assumed-online-ssr";

export interface UseNetworkStatusOptions {
  /**
   * URL to probe for real connectivity.
   * Prefer your own backend /health or /ping endpoint.
   * Defaults to a common lightweight endpoint.
   */
  probeUrl?: string;
  /** HTTP method for the probe (HEAD preferred when supported). */
  method?: "HEAD" | "GET";
  /** How often to re-probe while app is visible (ms). Set 0 to disable. */
  intervalMs?: number;
  /** Abort the probe if it takes longer than this (ms). */
  timeoutMs?: number;
  /** Number of retry attempts per probe cycle (with small backoff). */
  retries?: number;
  /** Start as online on SSR to avoid hydration mismatch. */
  ssrAssumeOnline?: boolean;
}

export interface NetworkStatus {
  isOnline: boolean;
  reason: NetworkReason;
  lastChangedAt: number; // epoch ms
  latencyMs: number | null;
  refresh: () => void; // force re-check now
}

function safeNow() {
  return typeof Date !== "undefined" ? Date.now() : 0;
}

async function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

async function probeReachability(
  url: string,
  method: "HEAD" | "GET",
  timeoutMs: number,
  retries: number
): Promise<{ ok: boolean; latencyMs: number | null }> {
  const attempt = async (): Promise<{ ok: boolean; latencyMs: number | null }> => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    const start =
      typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();

    try {
      // In browsers, use no-store to avoid cached responses.
      // Decide fetch mode based on origin to avoid CORS rejections for default cross-origin endpoints.
      const isCrossOrigin =
        typeof window !== "undefined"
          ? (() => {
              try {
                const target = new URL(url, window.location.href);
                return target.origin !== window.location.origin;
              } catch {
                return true;
              }
            })()
          : true;

      const res = await fetch(url, {
        method,
        cache: "no-store",
        mode: isCrossOrigin ? "no-cors" : "same-origin",
        signal: controller.signal
      } as RequestInit);

      const end =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();
      clearTimeout(timer);

      // If fetch resolves, we likely have connectivity:
      // - opaque (no-cors) => online
      // - any HTTP status (>0) => online (we're measuring reachability, not service health)
      const ok = res.type === "opaque" || (typeof res.status === "number" ? res.status > 0 : true);
      return { ok, latencyMs: end - start };
    } catch {
      clearTimeout(timer);
      return { ok: false, latencyMs: null };
    }
  };

  for (let i = 0; i <= retries; i++) {
    const r = await attempt();
    if (r.ok) return r;
    // small exponential backoff: 150ms, 300ms, 600ms...
    if (i < retries) await sleep(150 * Math.pow(2, i));
  }
  return { ok: false, latencyMs: null };
}

export function useNetworkStatus(options: UseNetworkStatusOptions = {}): NetworkStatus {
  const {
    probeUrl = "https://www.gstatic.com/generate_204", // tiny 204 (works well on web/Capacitor); prefer your own /health in prod
    method = "GET",
    intervalMs = 15_000,
    timeoutMs = 4_000,
    retries = 1,
    ssrAssumeOnline = true
  } = options;

  const isBrowser = typeof window !== "undefined";
  const hasNavigator = typeof navigator !== "undefined";
  const initialOnline =
    isBrowser && hasNavigator && "onLine" in navigator
      ? ((navigator as any).onLine as boolean)
      : ssrAssumeOnline;

  const [state, setState] = useState<NetworkStatus>({
    isOnline: initialOnline,
    reason: isBrowser ? "init" : "assumed-online-ssr",
    lastChangedAt: safeNow(),
    latencyMs: null,
    refresh: () => undefined
  });

  // keep stable refs
  const optsRef = useRef({ probeUrl, method, timeoutMs, retries });
  optsRef.current = { probeUrl, method, timeoutMs, retries };

  const setOnline = (isOnline: boolean, reason: NetworkReason, latencyMs: number | null) => {
    setState((prev) => {
      if (prev.isOnline === isOnline && prev.reason === reason && prev.latencyMs === latencyMs) {
        return prev;
      }
      return {
        isOnline,
        reason,
        lastChangedAt: safeNow(),
        latencyMs,
        refresh: prev.refresh // retain reference
      };
    });
  };

  const doProbe = useMemo(
    () => async () => {
      const { probeUrl: u, method: m, timeoutMs: t, retries: r } = optsRef.current;
      const result = await probeReachability(u, m, t, r);
      if (result.ok) {
        setOnline(true, "probe-success", result.latencyMs);
      } else {
        setOnline(false, "probe-failed", null);
      }
    },
    []
  );

  // expose refresh with stable identity
  useEffect(() => {
    setState((prev) => ({ ...prev, refresh: doProbe }));
  }, [doProbe]);

  // wire browser online/offline + visibility handlers when available
  useEffect(() => {
    if (!isBrowser) return;

    const handleOnline = () => {
      // optimistic flip, then verify
      setOnline(true, "browser-online", null);
      void doProbe();
    };
    const handleOffline = () => {
      setOnline(false, "browser-offline", null);
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        // re-check on tab focus
        void doProbe();
      }
    };

    // Some runtimes (e.g., RN) don't have these events; guard adds.
    window.addEventListener?.("online", handleOnline);
    window.addEventListener?.("offline", handleOffline);
    document?.addEventListener?.("visibilitychange", handleVisibility);

    // Initial verification (don’t trust onLine alone)
    void doProbe();

    return () => {
      window.removeEventListener?.("online", handleOnline);
      window.removeEventListener?.("offline", handleOffline);
      document?.removeEventListener?.("visibilitychange", handleVisibility);
    };
  }, [doProbe, isBrowser]);

  // periodic re-check while visible
  useEffect(() => {
    if (!isBrowser || intervalMs <= 0) return;

    let id: number | undefined;

    const tick = async () => {
      // Only probe when tab/app is visible (if document exists)
      const visible = typeof document === "undefined" || document.visibilityState === "visible";
      if (visible) {
        await doProbe();
      }
      id = window.setTimeout(tick, intervalMs);
    };

    id = window.setTimeout(tick, intervalMs);
    return () => {
      if (id) clearTimeout(id);
    };
  }, [doProbe, intervalMs, isBrowser]);

  return state;
}

export default useNetworkStatus;
